import { IonButton, IonIcon, IonText, useIonRouter } from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import RCForm, { useForm } from 'rc-field-form';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import Field from '@components/Form/Field';
import Password from '@components/Form/Password';
import RoutePath from '@custom-types/RoutePath';
import { UserSession, VerifyType } from '@custom-types/UserSession';
import useToast from '@hooks/useToast';
import { generateRoute } from '@services/navigation-service';
import { setUser } from '@state/user';

import {
  ErrorCode,
  authenticate,
  errorMessages,
  sendEmailLink as sendEmailLinkFn,
} from './service';

const Form = styled(RCForm)`
  width: 100%;
`;
const EmailHolder = styled.span`
  font-weight: 800;
  font-size: 18px;
`;
const Button = styled(IonButton)`
  width: 100%;
`;
const BorderedText = styled(IonText)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
`;
const Line = styled.span`
  flex-grow: 1;
  height: 1px;
  margin: 10px 10px 0px;
  background: #00000026;
  @media (prefers-color-scheme: dark) {
    background: #ffffff26;
  }
`;
const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  margin: 3vh 0 0 0;
`;

const Heading = styled.div`
  margin: 3vh 0;
  text-align: center;
`;

interface Props {
  user: UserSession;
  resetVerifiedUser: () => void;
  setIsEmailLinkSent: (value: boolean) => void;
}

const PasswordForm = ({ user, resetVerifiedUser, setIsEmailLinkSent }: Props) => {
  const { push: pushRoute } = useIonRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingEmailLink, setIsSendingEmailLink] = useState(false);
  const showToast = useToast();
  const dispatch = useDispatch();

  const [form] = useForm();

  const onFinish = async () => {
    if (!user.email) {
      return;
    }

    const { password } = await form.validateFields();
    try {
      setIsLoading(true);
      const userSession = await authenticate({
        email: user.email,
        password,
        stage: VerifyType.Password,
      });
      dispatch(setUser(userSession));
      resetVerifiedUser();
      setIsLoading(false);
      pushRoute(generateRoute(RoutePath.EventList));
    } catch (errors) {
      setIsLoading(false);
      const type =
        (errors as { type: ErrorCode }).type === ErrorCode.InvalidEmailOrPassword
          ? ErrorCode.IncorrectPassword
          : undefined;

      form.setFields([{ name: 'password', errors: [errorMessages(type)] }]);
    }
  };

  const sendEmailLink = async () => {
    if (!user.email) {
      return;
    }
    setIsSendingEmailLink(true);
    try {
      await sendEmailLinkFn(user.email);
      showToast(t`Email Sent!`);
      setIsEmailLinkSent(true);
    } catch (error) {
      showToast(errorMessages((error as { type: ErrorCode }).type));
    } finally {
      setIsSendingEmailLink(false);
    }
  };

  return (
    <Form
      initialValues={{ password: '' }}
      form={form}
      name="password-form"
      data-testid="password-form"
      // skipcq: JS-0417, JS-0336
      onFinish={onFinish}
    >
      <IonText className="ion-text-center">
        <h1>{t`Almost Done!`}</h1>
      </IonText>
      <Heading>
        <EmailHolder>{user.email}</EmailHolder> <br />
        {t`Enter your password to log in.`}
      </Heading>
      <Field name="password" rules={[{ required: true, message: t`Password is required.` }]}>
        <Password label={t`Password`} />
      </Field>
      <ButtonGroup>
        <Button type="submit" role="button" expand="block" disabled={isLoading}>
          {t`Login`}
        </Button>
        <Button
          type="button"
          role="button"
          expand="block"
          fill="outline"
          onClick={resetVerifiedUser}
        >
          <IonIcon slot="start" icon={arrowBack} />
          {t`Back`}
        </Button>
      </ButtonGroup>
      <Button
        type="button"
        role="button"
        fill="clear"
        routerLink={generateRoute(RoutePath.ForgotPassword, { email: user.email })}
      >
        {t`Forgot Password?`}
      </Button>
      <BorderedText>
        <Line />
        <h5>{t`or`}</h5>
        <Line />
      </BorderedText>
      <Button
        type="button"
        role="button"
        fill="clear"
        // skipcq: JS-0417, JS-0336
        onClick={sendEmailLink}
        disabled={isSendingEmailLink}
      >{t`Email Me A Login Link`}</Button>
    </Form>
  );
};

export default PasswordForm;
